/**
 * v3.19-1427 CHAT-EMBED · In-Hub Google Chat surface — UI polish.
 *
 * Ships a modern messenger experience on top of the existing OAuth
 * connection.  No new scopes, no new APIs, no architecture changes.
 *
 * Polish shipped this sprint:
 *   • Sidebar cards with initials avatars, real DM participant names,
 *     last message preview, relative timestamp, sorted by recency.
 *   • Search input above the sidebar (client-side, matches people
 *     and rooms).
 *   • "+ New Chat" dialog: searches Hub users first, then Google
 *     Chat contacts (memberships of existing spaces).  Click →
 *     opens existing DM inline or falls back to Google Chat.
 *   • Message thread rewritten as chat bubbles — outgoing right
 *     (emerald), incoming left (slate).  Sender name shown only on
 *     speaker changes.  Timestamps.  Auto-scroll to newest.
 *   • Composer: multi-line textarea, Enter = Send, Shift+Enter =
 *     newline, dedicated Send button.
 *   • Polling suspended when tab hidden; interval 20s.
 *   • Escape-hatch relabelled "Open in Google Chat".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import {
  MessageSquare, ExternalLink, Loader2, Send, RefreshCw, AlertCircle,
  Plus, Hash, User, Sparkles, Search as SearchIcon, X,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const POLL_MS = 20 * 1000;

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
};

const timeOfDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const initialsFrom = (name, email) => {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Deterministic emerald-family tint per initial letter so cards
// don't all look identical.  Palette matches the Hub design system.
const AVATAR_TONES = [
  'bg-emerald-100 text-emerald-800',
  'bg-teal-100 text-teal-800',
  'bg-slate-200 text-slate-800',
  'bg-amber-100 text-amber-800',
  'bg-sky-100 text-sky-800',
  'bg-violet-100 text-violet-800',
  'bg-rose-100 text-rose-800',
];
const toneFor = (seed) => {
  const s = (seed || '?').charCodeAt(0) || 0;
  return AVATAR_TONES[s % AVATAR_TONES.length];
};

function Avatar({ url, initials, size = 'md', tone }) {
  const sz = size === 'sm' ? 'h-7 w-7 text-[10px]' : size === 'lg' ? 'h-10 w-10 text-sm' : 'h-8 w-8 text-xs';
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`${sz} rounded-full object-cover border border-slate-200 shrink-0`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <div
      className={`${sz} rounded-full flex items-center justify-center font-semibold shrink-0 ${tone || toneFor(initials)}`}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

export default function InternalMessagingTab() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [spaces, setSpaces] = useState([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [activeSpace, setActiveSpace] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [apiNotEnabled, setApiNotEnabled] = useState(false);
  const [filter, setFilter] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const scrollRef = useRef(null);

  // ---------------------------------------------------------------- boot
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get('/google/chat/status');
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setStatusLoading(false);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    try {
      const r = await api.get('/google/connect');
      const url = r.data?.authorization_url;
      if (url) window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to start Google connection');
    }
  };

  // -------------------------------------------------------------- spaces
  const loadSpaces = useCallback(async () => {
    if (!status?.connected || !status?.has_chat_scopes) return;
    setSpacesLoading(true);
    setError(null);
    setApiNotEnabled(false);
    try {
      const r = await api.get('/google/chat/spaces');
      const items = r.data?.items || [];
      setSpaces(items);
      if (items.length && !activeSpace) setActiveSpace(items[0]);
    } catch (e) {
      if (e?.response?.status === 424) setApiNotEnabled(true);
      else setError(e?.response?.data?.detail || 'Failed to load spaces');
    } finally {
      setSpacesLoading(false);
    }
  }, [status, activeSpace]);
  useEffect(() => { loadSpaces(); }, [loadSpaces]);

  // ------------------------------------------------------------ messages
  const loadMessages = useCallback(async (silent = false) => {
    if (!activeSpace) { setMessages([]); return; }
    if (!silent) setMsgLoading(true);
    try {
      const r = await api.get(`/google/chat/spaces/${activeSpace.id}/messages`);
      setMessages(r.data?.items || []);
    } catch (e) {
      if (!silent) setError(e?.response?.data?.detail || 'Failed to load messages');
    } finally {
      if (!silent) setMsgLoading(false);
    }
  }, [activeSpace]);
  useEffect(() => { loadMessages(false); }, [loadMessages]);

  // Poll every POLL_MS ONLY while:
  //   - a space is selected
  //   - the browser tab is visible
  // If the user hides the tab, we suspend; on re-focus we do one
  // immediate refresh and resume the timer.
  useEffect(() => {
    if (!activeSpace) return;
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = window.setInterval(() => loadMessages(true), POLL_MS);
    };
    const stop = () => {
      if (timer) { window.clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadMessages(true);
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeSpace, loadMessages]);

  // Auto-scroll to newest on load or on new incoming.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, activeSpace?.id]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !activeSpace) return;
    setSending(true);
    try {
      const r = await api.post(`/google/chat/spaces/${activeSpace.id}/messages`, { text });
      setMessages((prev) => [...prev, r.data]);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Enter = send, Shift+Enter = newline.
  const onComposerKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const chatLaunchUrl = 'https://chat.google.com/?authuser=0';

  // Filter conversation list by name / preview.
  const filteredSpaces = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return spaces;
    return spaces.filter((s) => {
      const name = (s.display_name || '').toLowerCase();
      const email = (s.participant?.email || '').toLowerCase();
      const preview = (s.last_message || '').toLowerCase();
      return name.includes(q) || email.includes(q) || preview.includes(q);
    });
  }, [spaces, filter]);

  // The caller's Google email — used to determine which chat
  // messages are "outgoing" (right-side bubbles).
  const callerEmail = (status?.email || '').toLowerCase();

  // --------------------------------------------------------------- render
  if (statusLoading) {
    return (
      <div className="space-y-4" data-testid="internal-messaging-tab">
        <Card><CardContent className="py-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking Google connection…
        </CardContent></Card>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="space-y-4" data-testid="internal-messaging-tab">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-700" /> Internal Messaging · Google Chat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-slate-700">
              Connect your Google account to bring your Chat spaces inside Hub.
              The same connection powers Company Email — no separate login.
            </div>
            <Button onClick={connect} data-testid="internal-messaging-connect-google">
              <Plus className="h-4 w-4 mr-1" /> Connect Google Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status.reconnect_required) {
    return (
      <div className="space-y-4" data-testid="internal-messaging-tab">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-700" /> Internal Messaging · Google Chat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Your Google connection <span className="font-semibold">{status.email}</span> is missing Chat scopes.
                Reconnect to grant read/send access — Hub will then render your spaces inline (no new tab).
              </div>
            </div>
            <Button onClick={connect} data-testid="internal-messaging-reconnect-google">
              <RefreshCw className="h-4 w-4 mr-1" /> Reconnect Google Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (apiNotEnabled) {
    return (
      <div className="space-y-4" data-testid="internal-messaging-tab">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-700" /> Internal Messaging · Google Chat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="chat-api-disabled">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Google Chat API is not enabled in this GCP project.</div>
                  <div className="mt-1">
                    A GCP admin must enable the Chat API, then wait ~1 minute for propagation. After enablement, refresh this tab.
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild size="sm">
                  <a href="https://console.cloud.google.com/apis/library/chat.googleapis.com" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open GCP Console · Enable Chat API
                  </a>
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setApiNotEnabled(false); loadStatus(); loadSpaces(); }}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> I&apos;ve enabled it — Try again
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="internal-messaging-tab">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-emerald-700" />
            Internal Messaging · Google Chat
            <span className="text-[11px] text-slate-500 font-normal">as {status.email}</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => { loadSpaces(); loadMessages(false); }} title="Refresh" data-testid="chat-refresh-btn">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button asChild size="sm" variant="outline" title="Open the full Google Chat UI in a new tab">
              <a href={chatLaunchUrl} target="_blank" rel="noopener noreferrer" data-testid="chat-open-in-google">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in Google Chat
              </a>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800 flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3 h-[600px]">
            {/* ----------------------------------------------------- SIDEBAR */}
            <div className="border border-slate-200 rounded-md overflow-hidden flex flex-col bg-white" data-testid="chat-spaces-panel">
              <div className="border-b bg-slate-50 p-2 space-y-2">
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search people or spaces"
                    className="pl-8 h-8 text-sm"
                    data-testid="chat-sidebar-search"
                  />
                  {filter && (
                    <button
                      type="button"
                      onClick={() => setFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Button
                  size="sm"
                  className="w-full h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => setNewChatOpen(true)}
                  data-testid="chat-new-chat-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> New Chat
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {spacesLoading && (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {!spacesLoading && filteredSpaces.length === 0 && spaces.length === 0 && (
                  <div className="p-3 text-xs text-slate-500 flex items-start gap-1">
                    <Sparkles className="h-3 w-3 mt-0.5" /> No spaces yet. Create one in Google Chat and it will appear here.
                  </div>
                )}
                {!spacesLoading && filteredSpaces.length === 0 && spaces.length > 0 && (
                  <div className="p-3 text-xs text-slate-500 italic">No matches for &ldquo;{filter}&rdquo;.</div>
                )}
                {filteredSpaces.map((s) => {
                  const isDM = s.is_dm ?? (s.type === 'DIRECT_MESSAGE' || s.type === 'DM');
                  const active = activeSpace?.id === s.id;
                  const avatarUrl = s.participant?.avatar_url || null;
                  const initials = s.participant?.initials || s.initials || initialsFrom(s.display_name);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => setActiveSpace(s)}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex items-start gap-2.5 ${active ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                      data-testid={`chat-space-${s.id}`}
                    >
                      {isDM ? (
                        <Avatar url={avatarUrl} initials={initials} />
                      ) : (
                        <div className="h-8 w-8 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                          <Hash className="h-4 w-4" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm ${active ? 'text-emerald-900 font-semibold' : 'text-slate-900 font-medium'}`}>
                            {s.display_name}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {relTime(s.last_message_time)}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {s.last_message || (isDM ? 'No messages yet' : 'Room')}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ----------------------------------------------------- THREAD */}
            <div className="border border-slate-200 rounded-md overflow-hidden flex flex-col bg-white" data-testid="chat-thread-panel">
              <div className="text-sm font-semibold text-slate-900 px-3 py-2.5 border-b bg-slate-50 flex items-center gap-2.5">
                {activeSpace ? (
                  <>
                    {(activeSpace.is_dm ?? (activeSpace.type === 'DIRECT_MESSAGE' || activeSpace.type === 'DM')) ? (
                      <Avatar
                        url={activeSpace.participant?.avatar_url}
                        initials={activeSpace.participant?.initials || initialsFrom(activeSpace.display_name)}
                        size="sm"
                      />
                    ) : (
                      <div className="h-7 w-7 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center">
                        <Hash className="h-3.5 w-3.5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{activeSpace.display_name}</div>
                      {activeSpace.participant?.email && (
                        <div className="text-[11px] text-slate-500 font-normal truncate">
                          {activeSpace.participant.email}
                        </div>
                      )}
                    </div>
                  </>
                ) : <span className="text-slate-500 font-normal">Select a conversation</span>}
              </div>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-1.5 bg-slate-50/60"
                data-testid="chat-messages-scroll"
              >
                {msgLoading && (
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading messages…
                  </div>
                )}
                {!msgLoading && activeSpace && messages.length === 0 && (
                  <div className="text-xs text-slate-500 italic text-center py-8">
                    No messages yet.  Say hello below.
                  </div>
                )}
                {messages.map((m, idx) => {
                  // "Outgoing" heuristic — Google Chat messages don't
                  // return the sender's email on user tokens, so we
                  // detect self via displayName match on the mailbox
                  // display name.  Fallback: previous message check.
                  const senderName = (m.sender_display || '').trim();
                  const selfName = (status?.display_name || '').trim();
                  const isMine = !!senderName && !!selfName && senderName.toLowerCase() === selfName.toLowerCase();
                  const prev = idx > 0 ? messages[idx - 1] : null;
                  const showSender = !prev || (prev.sender_display || '') !== (m.sender_display || '');
                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col ${isMine ? 'items-end' : 'items-start'}`}
                      data-testid={`chat-msg-${m.id}`}
                    >
                      {showSender && !isMine && (
                        <div className="text-[11px] text-slate-500 mb-0.5 ml-1">
                          {m.sender_display || 'Unknown'}
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                          isMine
                            ? 'bg-emerald-600 text-white rounded-br-md'
                            : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                        }`}
                      >
                        {m.text || <span className="italic opacity-75">(non-text content — open in Google Chat)</span>}
                      </div>
                      <div className={`text-[10px] mt-0.5 ${isMine ? 'text-slate-400 mr-1' : 'text-slate-400 ml-1'}`}>
                        {timeOfDay(m.create_time)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {activeSpace && (
                <div className="border-t border-slate-200 p-2.5 bg-white flex items-end gap-2" data-testid="chat-composer">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onComposerKey}
                    rows={2}
                    placeholder={`Message ${activeSpace.display_name}…   (Enter to send · Shift+Enter for new line)`}
                    className="min-h-[52px] text-sm resize-none"
                    data-testid="chat-composer-input"
                  />
                  <Button
                    size="sm"
                    onClick={send}
                    disabled={!draft.trim() || sending}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-[52px] px-4"
                    data-testid="chat-composer-send"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            Rendered via Google Chat REST API · your existing OAuth session · updates every 20s while this tab is visible.
            Full Chat features (rich cards, calls, huddles) are one click away via <span className="whitespace-nowrap">Open in Google Chat</span>.
          </div>
        </CardContent>
      </Card>

      <NewChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        onOpenSpace={(sp) => {
          // Optimistically prepend the new DM to the sidebar so
          // it's visible even before the next /spaces refresh, and
          // then kick off a full refresh in the background.
          setSpaces((prev) => {
            if (prev.some((p) => p.id === sp.id)) return prev;
            return [sp, ...prev];
          });
          setActiveSpace(sp);
          setNewChatOpen(false);
          loadSpaces();
        }}
        spaces={spaces}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// v3.19-1427 · New Chat dialog
//   Two-tier search: Hub users first, then Google Chat contacts from
//   existing space memberships.  Clicking a contact either opens the
//   existing DM inline (if already known) or offers the Google Chat
//   escape hatch — starting a brand new DM requires the
//   chat.spaces.create scope which we intentionally have not requested.
// ---------------------------------------------------------------------------
function NewChatDialog({ open, onOpenChange, onOpenSpace, spaces }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [findingFor, setFindingFor] = useState(null);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); return; }
    // Kick off a default listing so the dialog isn't empty.
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/google/chat/contacts', { params: { q: '', limit: 20 } });
        if (!cancelled) setResults(r.data?.items || []);
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Debounced query.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/google/chat/contacts', { params: { q: query, limit: 25 } });
        setResults(r.data?.items || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [query, open]);

  const startChat = async (person) => {
    // 1. If /contacts already surfaced an existing DM for this person
    //    (Chat-tier result), open it inline immediately — zero API
    //    calls, zero leaving Hub.
    if (person.existing_space_id) {
      const sp = spaces.find((s) => s.id === person.existing_space_id);
      if (sp) { onOpenSpace(sp); return; }
    }

    // 2. Ask the backend to find-or-CREATE the DM via spaces.setup.
    //    Only fall back to the escape hatch on a documented Google
    //    limitation (cross-domain / external-user restriction).
    setFindingFor(person.email || person.user_id);
    try {
      const r = await api.post('/google/chat/dms', {
        user_email: person.email || undefined,
        user_id:    person.user_id || undefined,
      });
      const space = r.data;
      if (space?.space_id) {
        // Optimistically inject into the sidebar so it's clickable
        // without waiting for the next /spaces refresh.
        const injected = {
          id:            space.space_id,
          name:          space.space_name,
          display_name:  space.display_name || person.display_name || person.email || 'Direct message',
          type:          'DIRECT_MESSAGE',
          is_dm:         true,
          initials:      person.initials || initialsFrom(person.display_name, person.email),
          participant: {
            display_name: person.display_name,
            email:        person.email,
            avatar_url:   person.avatar_url,
            initials:     person.initials || initialsFrom(person.display_name, person.email),
          },
          last_message:      '',
          last_message_time: null,
        };
        onOpenSpace(injected);
        if (space.created) toast.success(`Started chat with ${person.display_name}`);
        return;
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;

      // 428 → scope missing → tell the user to reconnect (no
      // external hop — the tab itself will render the Reconnect
      // card once they retry).
      if (status === 428) {
        toast.error(typeof detail === 'string'
          ? detail
          : 'Reconnect your Google account to grant Chat access, then try again.');
        return;
      }

      // 424 → Chat API not enabled in GCP.
      if (status === 424) {
        toast.error('Google Chat API is not enabled in the GCP project. Ask an admin to enable it.');
        return;
      }

      // 409 → documented Google restriction (cross-domain /
      //       external user).  This is the ONLY case that
      //       legitimately falls back to the escape hatch.
      if (status === 409) {
        const url = detail?.fallback_url
          || (person.email
              ? `https://mail.google.com/chat/u/0/#chat/dm/${encodeURIComponent(person.email)}`
              : 'https://chat.google.com/?authuser=0');
        toast.info(detail?.message
          || `Google Chat won't create this DM inline — opening Google Chat as a fallback.`);
        window.open(url, '_blank', 'noopener,noreferrer');
        onOpenChange(false);
        return;
      }

      toast.error((typeof detail === 'string' ? detail : null) || 'Could not start chat');
    } finally {
      setFindingFor(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-700" /> New Chat
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
              className="pl-8"
              data-testid="new-chat-search"
            />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Hub users first · Google Chat contacts second
          </div>
          <div className="max-h-[360px] overflow-y-auto border border-slate-100 rounded-md">
            {loading && (
              <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Searching…
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-4 text-xs text-slate-500 italic">
                No people matched. Try a different name or email.
              </div>
            )}
            {results.map((p) => {
              const busy = findingFor && (findingFor === p.email || findingFor === p.user_id);
              return (
                <button
                  type="button"
                  key={`${p.source}-${p.user_id || p.email || p.display_name}`}
                  onClick={() => startChat(p)}
                  disabled={!!findingFor}
                  className="w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-emerald-50 disabled:opacity-60"
                  data-testid={`new-chat-result-${p.source}-${p.email || p.user_id || ''}`}
                >
                  <Avatar
                    url={p.avatar_url}
                    initials={p.initials || initialsFrom(p.display_name, p.email)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {p.display_name}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.email || (p.source === 'chat' ? 'Google Chat contact' : 'Hub user')}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {p.source === 'hub' ? (
                      <Badge variant="outline" className="text-[10px] py-0 h-4 border-emerald-200 text-emerald-700 bg-emerald-50">Hub</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] py-0 h-4 border-slate-200 text-slate-700">Chat</Badge>
                    )}
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
